import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";

import { useOnboardingState } from "./useOnboardingState";

const INDUSTRIES = [
  "Retail",
  "SaaS",
  "Ecommerce",
  "Finance",
  "Marketing",
  "Healthcare",
  "Education",
  "Consulting",
  "Real Estate",
  "Media",
  "Technology",
];

const ROLES = [
  "Founder / CEO",
  "Operations",
  "Marketing",
  "Sales",
  "Engineering",
  "Finance",
  "Customer Support",
  "Product",
  "HR",
];

const INTENTS = [
  { id: "customer_support", label: "Customer Support" },
  { id: "sales", label: "Sales & CRM" },
  { id: "project_management", label: "Project Management" },
  { id: "analytics", label: "Analytics & Reporting" },
  { id: "communication", label: "Team Communication" },
  { id: "content_creation", label: "Content Creation" },
  { id: "accounting", label: "Accounting & Invoicing" },
  { id: "development", label: "Software Development" },
  { id: "marketing_automation", label: "Marketing Automation" },
  { id: "scheduling", label: "Scheduling & Calendar" },
  { id: "knowledge_management", label: "Knowledge Management" },
  { id: "ecommerce_ops", label: "Ecommerce Operations" },
];

/** Step 1: Capture industry, role, and intents. Buffered in wizard state. */
export function ContextStep() {
  const { industry, role, intents, setIndustry, setRole, setIntents } =
    useOnboardingState();
  const navigate = useNavigate();
  const [otherIndustry, setOtherIndustry] = useState("");
  const [otherRole, setOtherRole] = useState("");

  const effectiveIndustry = industry === "Other" ? otherIndustry : industry;
  const effectiveRole = role === "Other" ? otherRole : role;

  function toggleIntent(id: string) {
    setIntents(
      intents.includes(id) ? intents.filter((i) => i !== id) : [...intents, id],
    );
  }

  function handleContinue() {
    // Buffer the effective values
    if (industry === "Other" && otherIndustry) setIndustry(otherIndustry);
    if (role === "Other" && otherRole) setRole(otherRole);
    navigate("/onboard/workspace");
  }

  const canContinue = (effectiveIndustry || industry) && (effectiveRole || role);

  return (
    <Stack spacing={3}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Tell us about yourself
      </Typography>

      {/* Industry */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          What industry are you in?
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {INDUSTRIES.map((ind) => (
            <Chip
              key={ind}
              label={ind}
              variant={industry === ind ? "filled" : "outlined"}
              color={industry === ind ? "primary" : "default"}
              onClick={() => setIndustry(ind)}
            />
          ))}
          <Chip
            label="Other"
            variant={industry === "Other" ? "filled" : "outlined"}
            color={industry === "Other" ? "primary" : "default"}
            onClick={() => setIndustry("Other")}
          />
        </Box>
        {industry === "Other" && (
          <TextField
            size="small"
            placeholder="Your industry"
            value={otherIndustry}
            onChange={(e) => setOtherIndustry(e.target.value)}
            sx={{ mt: 1 }}
            fullWidth
          />
        )}
      </Box>

      {/* Role */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          What's your role?
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {ROLES.map((r) => (
            <Chip
              key={r}
              label={r}
              variant={role === r ? "filled" : "outlined"}
              color={role === r ? "primary" : "default"}
              onClick={() => setRole(r)}
            />
          ))}
          <Chip
            label="Other"
            variant={role === "Other" ? "filled" : "outlined"}
            color={role === "Other" ? "primary" : "default"}
            onClick={() => setRole("Other")}
          />
        </Box>
        {role === "Other" && (
          <TextField
            size="small"
            placeholder="Your role"
            value={otherRole}
            onChange={(e) => setOtherRole(e.target.value)}
            sx={{ mt: 1 }}
            fullWidth
          />
        )}
      </Box>

      {/* Intents */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          What will you use Artie for? (select all that apply)
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {INTENTS.map((intent) => (
            <Chip
              key={intent.id}
              label={intent.label}
              variant={intents.includes(intent.id) ? "filled" : "outlined"}
              color={intents.includes(intent.id) ? "primary" : "default"}
              onClick={() => toggleIntent(intent.id)}
            />
          ))}
        </Box>
      </Box>

      <Button
        variant="contained"
        size="large"
        disabled={!canContinue}
        onClick={handleContinue}
        sx={{ alignSelf: "flex-end" }}
      >
        Continue
      </Button>
    </Stack>
  );
}
